import React, { useState, useEffect, useMemo } from "react";
import WeatherChart from "./WeatherChart";
import TabSelector from "../components/TabSelector";
import { CHART_CONFIGS } from "../misc/chartConfigs";
import "../styles/ExpandableCard.css";
import IconTemp from "../assets/icon-temp-color.png";
import IconRain from "../assets/icon-rain-color.png";
import IconWind from "../assets/icon-wind-color.png";
import IconCloud from "../assets/icon-cloud.png";
import { Trash2, Lock } from "lucide-react";

const INCH_TO_MM = 25.4;

export default function ExpandableCard({
  venue,
  forecastData,
  unitSettings,
  onRemove,
}) {
  /* ───────── state ───────── */
  const [selectedDate, setSelectedDate] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("temperature");

  /* ───────── choose default date ───────── */
  useEffect(() => {
    if (venue.date) {
      setSelectedDate(venue.date);
    } else if (forecastData.length) {
      const d = new Date(Number(forecastData[0].forecast_date));
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      setSelectedDate(`${yyyy}-${mm}-${dd}`);
    }
  }, [venue, forecastData]);

  /* ───────── helpers ───────── */
  const formatLocalDate = (ts) => {
    const d = new Date(Number.isNaN(+ts) ? ts : Number(ts));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  /* ───────── filter by chosen date ───────── */
  const filteredData = useMemo(
    () =>
      forecastData.filter(
        (d) => formatLocalDate(d.forecast_date) === selectedDate
      ),
    [selectedDate, forecastData]
  );

  /* ───────── shape for WeatherChart ───────── */
  const formattedData = useMemo(
    () =>
      filteredData.map((dp) => ({
        date: new Date(
          Number.isNaN(+dp.forecast_date)
            ? dp.forecast_date
            : Number(dp.forecast_date)
        ).getTime(),
        temperature: +dp.temperature || 0,
        temperatureMin: +dp.temperatureMin || 0,
        temperatureMax: +dp.temperatureMax || 0,
        precipitation: (+dp.precipitation || 0) * INCH_TO_MM,
        cloud: +dp.cloud || 0,
        windSpeed: +dp.windSpeed || 0,
        windDirection: +dp.windDirection || 0,
        watts: +dp.watts || 0,
        wattsMin: +dp.wattsMin || 0,
        wattsMax: +dp.wattsMax || 0,
        rh2m: +dp.rh2m || 0,
        rh2mMin: +dp.rh2mMin || 0,
        rh2mMax: +dp.rh2mMax || 0,
      })),
    [filteredData]
  );

  /* ───────── quick stats (insights) ───────── */
  const insights = useMemo(() => {
    if (!formattedData.length) return null;
    const maxTemp = Math.max(...formattedData.map((d) => d.temperatureMax));
    const minTemp = Math.min(...formattedData.map((d) => d.temperatureMin));
    const avgTemp =
      formattedData.reduce((s, d) => s + d.temperature, 0) /
      formattedData.length;
    const totalPrecip = formattedData.reduce((s, d) => s + d.precipitation, 0);
    const maxPrecipRate = Math.max(
      ...formattedData.map((d) => d.precipitation)
    );
    const avgWind =
      formattedData.reduce((s, d) => s + d.windSpeed, 0) / formattedData.length;
    const avgCloud =
      formattedData.reduce((s, d) => s + d.cloud, 0) / formattedData.length;
    return {
      maxTemp,
      minTemp,
      avgTemp,
      totalPrecip,
      maxPrecipRate,
      avgWind,
      avgCloud,
    };
  }, [formattedData]);

  /* ───────── TRIAL detection & allowed tab ───────── */
  const trialInfo = useMemo(() => {
    if (!forecastData.length) return { isTrial: false, allowedTab: null };

    // First row is enough – every row has the same shape
    const row = forecastData[0];

    // list of “groups” we expect, in priority order (pick whichever matches first)
    const groups = [
      {
        keys: ["temperature", "temperatureMin", "temperatureMax"],
        tab: "temperature",
      },
      { keys: ["windSpeed", "windDirection"], tab: "windSpeed" },
      { keys: ["precipitation"], tab: "precipitation" },
      { keys: ["cloud"], tab: "cloud" },
      { keys: ["watts", "wattsMin", "wattsMax"], tab: "allSky" },
      { keys: ["rh2m", "rh2mMin", "rh2mMax"], tab: "rh2m" },
    ];

    // Which group actually has numeric data?
    const match = groups.find((g) =>
      g.keys.some((k) => typeof row[k] === "number" && !Number.isNaN(row[k]))
    );

    // If EVERY group has data -> paid plan
    const allHaveData = groups.every((g) =>
      g.keys.some((k) => typeof row[k] === "number" && !Number.isNaN(row[k]))
    );

    if (allHaveData || !match) return { isTrial: false, allowedTab: null };

    return { isTrial: true, allowedTab: match.tab };
  }, [forecastData]);

  const { isTrial, allowedTab } = trialInfo;
  const isLockedView = isTrial && activeTab !== allowedTab;

  /* ───────── date bounds for WeatherChart ───────── */
  const [y, mo, da] = selectedDate.split("-").map(Number);
  const startDateObj = new Date(y, mo - 1, da);
  const endDateObj = new Date(y, mo - 1, da, 23, 59, 59, 999);

  /* ───────── render ───────── */
  return (
    <div className="expandable-card">
      {onRemove && (
        <div className="card-toolbar">
          <span
            className="tooltip"
            data-tooltip="Removing this card is irreversible"
          >
            <button
              className="toolbar-icon"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(venue.name, selectedDate);
              }}
              aria-label="Remove this card"
              title="Remove this card"
            >
              <Trash2 className="trash-icon" />
            </button>
          </span>
        </div>
      )}

      {/* ─── Header ─── */}
      <div className="card-header" onClick={() => setExpanded((e) => !e)}>
        <div className="header-content">
          <h3>{venue.name}</h3>
          <p>
            {selectedDate
              ? new Date(`${selectedDate}T00:00:00`).toLocaleDateString()
              : "Select a date"}
          </p>
        </div>
        <button
          className="toggle-button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((e) => !e);
          }}
        >
          {expanded ? "Collapse" : "Expand"}
        </button>
      </div>

      {/* ─── Body ─── */}
      {expanded && (
        <div className="card-body flex-row">
          {/* ---------- CHART COLUMN ---------- */}
          <div className="chart-column">
            <p className="tab-instruction">
              Click the icons below to switch between different weather
              variables.
            </p>

            <TabSelector activeTab={activeTab} onTabChange={setActiveTab} />

            <div className={`chart-container ${isLockedView ? "locked" : ""}`}>
              {formattedData.length ? (
                <WeatherChart
                  data={formattedData}
                  startDate={startDateObj}
                  endDate={endDateObj}
                  interval={0}
                  unitSettings={unitSettings}
                  chartConfig={CHART_CONFIGS[activeTab]}
                  calculateDomain={(data, minKey, maxKey, margin) => {
                    const mins = data.map((i) => i[minKey]);
                    const maxs = data.map((i) => i[maxKey]);
                    return [
                      Math.min(...mins) - margin,
                      Math.max(...maxs) + margin,
                    ];
                  }}
                />
              ) : (
                <p>No data for this date.</p>
              )}

              {/* overlay for trial‑locked tabs */}
              {isLockedView && (
                <div className="locked-overlay">
                  <Lock size={20} style={{ marginRight: 6 }} />
                  Upgrade&nbsp;your&nbsp;plan&nbsp;to&nbsp;unlock
                </div>
              )}
            </div>
          </div>

          {/* ---------- INSIGHTS COLUMN ---------- */}
          <div className={`insights-sidebar ${isLockedView ? "locked" : ""}`}>
            {insights && (
              <>
                <div className="insight-item">
                  <img
                    src={IconTemp}
                    alt="Temperature"
                    className="insight-icon"
                  />
                  <div className="insight-content">
                    <div className="insight-individual-stat">
                      <p className="insight-value">
                        {insights.avgTemp.toFixed(1)}°{unitSettings.t2m_units}
                      </p>
                      <p className="insight-label">Avg Temp</p>
                    </div>
                    <div className="insight-individual-stat">
                      <p className="insight-value">
                        {insights.maxTemp.toFixed(2)}°{unitSettings.t2m_units}
                      </p>
                      <p className="insight-label">Max Temp</p>
                    </div>
                    <div className="insight-individual-stat">
                      <p className="insight-value">
                        {insights.minTemp.toFixed(2)}°{unitSettings.t2m_units}
                      </p>
                      <p className="insight-label">Min Temp</p>
                    </div>
                  </div>
                </div>

                <div className="insight-item">
                  <img
                    src={IconRain}
                    alt="Precipitation"
                    className="insight-icon"
                  />
                  <div className="insight-content">
                    <div className="insight-individual-stat">
                      <p className="insight-value">
                        {insights.totalPrecip.toFixed(1)} mm
                      </p>
                      <p className="insight-label">Total Precip</p>
                    </div>
                    <div className="insight-individual-stat">
                      <p className="insight-value">
                        {insights.maxPrecipRate.toFixed(1)} mm / hr
                      </p>
                      <p className="insight-label">Peak Rate</p>
                    </div>
                  </div>
                </div>

                <div className="insight-item">
                  <img src={IconWind} alt="Avg Wind" className="insight-icon" />
                  <p className="insight-value">
                    {insights.avgWind.toFixed(2)} {unitSettings.ws2m_units}
                  </p>
                  <p className="insight-label">Avg Wind</p>
                </div>

                <div className="insight-item">
                  <img
                    src={IconCloud}
                    alt="Cloud Cover"
                    className="insight-icon"
                  />
                  <p className="insight-value">
                    {insights.avgCloud.toFixed(0)}%
                  </p>
                  <p className="insight-label">Cloud Cover</p>
                </div>
              </>
            )}

            {/* overlay for trial‑locked insights */}
            {isLockedView && (
              <div className="locked-overlay">
                <Lock size={20} style={{ marginRight: 6 }} />
                Upgrade&nbsp;to&nbsp;see&nbsp;insights
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
